import { httpTokenCommon } from "../helper/http-common";
import {
  CodSummary,
  PortalAccountSummary,
  RepCustomerSummary,
  SalesRepOption,
  SalesRepProfile,
  SalesRepRegisterPayload,
  SalesRepSyncResult,
  SalesRepSyncStatus,
} from "../types/salesRepEnrolment";

const normalizeSalesRep = (raw: Record<string, unknown>): SalesRepOption => ({
  salesRepCode: String(raw.salesRepCode ?? raw.SalesRepCode ?? "").trim(),
  salesRepName: String(raw.salesRepName ?? raw.SalesRepName ?? "").trim(),
  repRole: String(raw.repRole ?? raw.RepRole ?? "").trim() || undefined,
  salesManagerCode: String(raw.salesManagerCode ?? raw.SalesManagerCode ?? "").trim(),
  salesManagerName: String(raw.salesManagerName ?? raw.SalesManagerName ?? "").trim(),
});

const normalizeCustomer = (raw: Record<string, unknown>): RepCustomerSummary => ({
  customerCode: String(raw.customerCode ?? raw.CustomerCode ?? "").trim(),
  customerName: String(raw.customerName ?? raw.CustomerName ?? "").trim(),
});

const normalizePortalAccount = (raw: Record<string, unknown>): PortalAccountSummary => ({
  exists: Boolean(raw.exists ?? raw.Exists),
  userName: String(raw.userName ?? raw.UserName ?? "").trim(),
  mustChangePassword: Boolean(raw.mustChangePassword ?? raw.MustChangePassword),
});

const normalizeCodSummary = (raw: Record<string, unknown>): CodSummary => ({
  collectedAmount: Number(raw.collectedAmount ?? raw.CollectedAmount ?? 0),
  pendingAmount: Number(raw.pendingAmount ?? raw.PendingAmount ?? 0),
  currency: String(raw.currency ?? raw.Currency ?? "IQD").trim() || "IQD",
});

const normalizeProfile = (raw: Record<string, unknown>): SalesRepProfile => {
  const portalRaw = (raw.portalAccount ?? raw.PortalAccount ?? {}) as Record<string, unknown>;
  const codRaw = (raw.codSummary ?? raw.CodSummary ?? {}) as Record<string, unknown>;
  const rep1 = Array.isArray(raw.customersRep1 ?? raw.CustomersRep1)
    ? (raw.customersRep1 ?? raw.CustomersRep1 as unknown[])
    : [];
  const rep2 = Array.isArray(raw.customersRep2 ?? raw.CustomersRep2)
    ? (raw.customersRep2 ?? raw.CustomersRep2 as unknown[])
    : [];

  return {
    salesRepCode: String(raw.salesRepCode ?? raw.SalesRepCode ?? "").trim(),
    salesRepName: String(raw.salesRepName ?? raw.SalesRepName ?? "").trim(),
    repRole: String(raw.repRole ?? raw.RepRole ?? "").trim(),
    salesManagerCode: String(raw.salesManagerCode ?? raw.SalesManagerCode ?? "").trim(),
    salesManagerName: String(raw.salesManagerName ?? raw.SalesManagerName ?? "").trim(),
    portalAccount: normalizePortalAccount(portalRaw),
    customersRep1: rep1.map((item) => normalizeCustomer(item as Record<string, unknown>)),
    customersRep2: rep2.map((item) => normalizeCustomer(item as Record<string, unknown>)),
    codSummary: normalizeCodSummary(codRaw),
  };
};

const normalizeSyncPayload = (raw: Record<string, unknown>): SalesRepSyncResult => ({
  status: String(raw.status ?? raw.Status ?? "").trim(),
  description: String(raw.description ?? raw.Description ?? "").trim() || undefined,
  userName: String(raw.userName ?? raw.UserName ?? "").trim() || undefined,
  tempPassword: String(raw.tempPassword ?? raw.TempPassword ?? "").trim() || undefined,
  mustChangePassword: Boolean(raw.mustChangePassword ?? raw.MustChangePassword),
});

class SalesRepEnrolmentService {
  async getSalesReps(): Promise<SalesRepOption[]> {
    const response = await httpTokenCommon.get("auth/sales-reps", { silent: true });
    const list = Array.isArray(response.data) ? response.data : [];
    return list
      .map((item) => normalizeSalesRep(item as Record<string, unknown>))
      .filter((rep) => rep.salesRepCode);
  }

  async searchReps(keyword: string): Promise<SalesRepOption[]> {
    const response = await httpTokenCommon.get("auth/sales-reps/search", {
      params: { keyword: keyword.trim() },
      silent: true,
    });
    const list = Array.isArray(response.data) ? response.data : [];
    return list
      .map((item) => normalizeSalesRep(item as Record<string, unknown>))
      .filter((rep) => rep.salesRepCode);
  }

  async getProfile(salesRepCode: string): Promise<SalesRepProfile> {
    const response = await httpTokenCommon.get(
      `auth/sales-reps/${encodeURIComponent(salesRepCode.trim())}`,
      { silent: true }
    );
    return normalizeProfile((response.data ?? {}) as Record<string, unknown>);
  }

  async getSyncStatus(salesRepCode: string): Promise<SalesRepSyncStatus> {
    const response = await httpTokenCommon.get("auth/sales-reps/syncstatus", {
      params: { salesRepCode: salesRepCode.trim() },
      silent: true,
    });
    const raw = (response.data ?? {}) as Record<string, unknown>;
    return {
      status: String(raw.status ?? raw.Status ?? "").trim(),
      description: String(raw.description ?? raw.Description ?? "").trim() || undefined,
      userName: String(raw.userName ?? raw.UserName ?? "").trim() || undefined,
    };
  }

  async syncRep(salesRepCode: string) {
    return httpTokenCommon.post(
      `auth/sales-reps/sync?salesRepCode=${encodeURIComponent(salesRepCode.trim())}`,
      undefined,
      { silent: true }
    );
  }

  async syncRepNormalized(salesRepCode: string): Promise<SalesRepSyncResult> {
    const response = await this.syncRep(salesRepCode);
    return normalizeSyncPayload((response.data ?? {}) as Record<string, unknown>);
  }

  registerSalesRep(payload: SalesRepRegisterPayload) {
    return httpTokenCommon.post("auth/register-salesrep", payload, { silent: true });
  }
}

const salesRepEnrolmentService = new SalesRepEnrolmentService();
export default salesRepEnrolmentService;
